const API_BASE_URL = 'https://backend.parasitos.cl/api/v1'

// Helper function to get headers with ngrok bypass
function getHeaders(): HeadersInit {
  return {
    'ngrok-skip-browser-warning': 'true',
    'Content-Type': 'application/json',
  }
}

export interface DiputadoPublic {
  id: string
  nombre: string
  partido: string
  bancada: string[]
  fecha_inicio: string
  fecha_fin: string
  email: string | null
  distrito_numero: number
  cantidad_sesiones: number
  image_path: string | null
}

export interface DiputadoStats {
  diputado_id: string | null
  fecha_inicio: string
  fecha_fin: string
  total_sesiones: number
  total_inasistencias: number
  inasistencias_injustificadas: number
  total_sesiones_mins: number
  atrasos: number
  sesiones_mins_efectivo: number
  porcentaje_sesiones_mins_efectivo: number
  porcentaje_inasistencias: number
  porcentaje_inasistencias_injustificadas: number
  porcentaje_atrasos: number
}

export interface DiputadosGeneralStats {
  fecha_inicio: string
  fecha_fin: string
  total_sesiones: number
  total_sesiones_mins: number
  promedio_inasistencias: number
  promedio_porcentaje_inasistencias: number
  promedio_inasistencias_injustificadas: number
  promedio_porcentaje_inasistencias_injustificadas: number
  promedio_atrasos: number
  promedio_porcentaje_atrasos: number
  promedio_sesiones_mins_efectivo: number
  promedio_porcentaje_mins_efectivo: number
}

export interface Top10DiputadoStat {
  diputado: DiputadoPublic
  stat_name: string
  stat_value: number
  stat_total: number
  stat_porcentaje: number
  desafuero: boolean
  guagua: boolean
}

export interface DiputadosPublic {
  data: DiputadoPublic[]
}

export interface Tops10DiputadosStat {
  data: Top10DiputadoStat[]
}

// Diputados API
export async function getDiputadoById(id: string): Promise<DiputadoPublic> {
  const response = await fetch(`${API_BASE_URL}/diputados/${id}`, {
    headers: getHeaders(),
  })
  if (!response.ok) {
    throw new Error('Failed to fetch diputado')
  }
  return response.json()
}

export async function getDiputadoStats(
  id: string,
  startDate: string,
  endDate: string,
): Promise<DiputadoStats> {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
  })
  const response = await fetch(
    `${API_BASE_URL}/diputados/stats/${id}?${params}`,
    {
      headers: getHeaders(),
    },
  )
  if (!response.ok) {
    throw new Error('Failed to fetch diputado stats')
  }
  return response.json()
}

export async function getGeneralStats(
  startDate: string,
  endDate: string,
): Promise<DiputadosGeneralStats> {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
  })
  const response = await fetch(`${API_BASE_URL}/diputados/stats?${params}`, {
    headers: getHeaders(),
  })
  if (!response.ok) {
    throw new Error('Failed to fetch general stats')
  }
  return response.json()
}

export async function searchDiputados(name: string): Promise<DiputadosPublic> {
  const response = await fetch(
    `${API_BASE_URL}/diputados/search/${encodeURIComponent(name)}`,
    {
      headers: getHeaders(),
    },
  )
  if (!response.ok) {
    throw new Error('Failed to search diputados')
  }
  return response.json()
}

export async function getAllDiputados(): Promise<DiputadosPublic> {
  const response = await fetch(`${API_BASE_URL}/diputados/`, {
    headers: getHeaders(),
  })
  if (!response.ok) {
    throw new Error('Failed to fetch diputados')
  }
  return response.json()
}

// Top 10 API
export async function getTop10Atrasos(
  startDate: string,
  endDate: string,
): Promise<Tops10DiputadosStat> {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
  })
  const response = await fetch(`${API_BASE_URL}/top-10/atrasos?${params}`, {
    headers: getHeaders(),
  })
  if (!response.ok) {
    throw new Error('Failed to fetch top 10 atrasos')
  }
  return response.json()
}

export async function getTop10Inasistencias(
  startDate: string,
  endDate: string,
): Promise<Tops10DiputadosStat> {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
  })
  const response = await fetch(
    `${API_BASE_URL}/top-10/inasistencias?${params}`,
    {
      headers: getHeaders(),
    },
  )
  if (!response.ok) {
    throw new Error('Failed to fetch top 10 inasistencias')
  }
  return response.json()
}

export async function getTop10InasistenciasInjustificadas(
  startDate: string,
  endDate: string,
): Promise<Tops10DiputadosStat> {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
  })
  const response = await fetch(
    `${API_BASE_URL}/top-10/inasistencias-injustificadas?${params}`,
    {
      headers: getHeaders(),
    },
  )
  if (!response.ok) {
    throw new Error('Failed to fetch top 10 inasistencias injustificadas')
  }
  return response.json()
}

export async function getTop10MenosTiempoCamara(
  startDate: string,
  endDate: string,
): Promise<Tops10DiputadosStat> {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
  })
  const response = await fetch(
    `${API_BASE_URL}/top-10/menos-tiempo-camara?${params}`,
    {
      headers: getHeaders(),
    },
  )
  if (!response.ok) {
    throw new Error('Failed to fetch top 10 menos tiempo camara')
  }
  return response.json()
}
